"use client";

import Link from "next/link";

type CtaButtonVariant = "primary" | "secondary" | "outline" | "green" | "ghost";
type CtaButtonSize = "sm" | "md" | "lg";

type CtaButtonBaseProps = {
  children: React.ReactNode;
  className?: string;
  variant?: CtaButtonVariant;
  size?: CtaButtonSize;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
};

type CtaButtonAsLink = CtaButtonBaseProps & {
  href: string;
  onClick?: never;
  type?: never;
  opensQuoteModal?: never;
  opensZendeskChat?: never;
};

type CtaButtonAsButton = CtaButtonBaseProps & {
  href?: never;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  opensQuoteModal?: never;
  opensZendeskChat?: never;
};

type CtaButtonAsQuoteModal = CtaButtonBaseProps & {
  href?: never;
  onClick?: never;
  type?: never;
  disabled?: never;
  opensQuoteModal: true;
  opensZendeskChat?: never;
};

type CtaButtonAsZendeskChat = CtaButtonBaseProps & {
  href?: never;
  onClick?: never;
  type?: never;
  disabled?: never;
  opensQuoteModal?: never;
  opensZendeskChat: true;
};

export type CtaButtonProps =
  | CtaButtonAsLink
  | CtaButtonAsButton
  | CtaButtonAsQuoteModal
  | CtaButtonAsZendeskChat;

const variantClasses: Record<CtaButtonVariant, string> = {
  // Hover must change background-color (not only ::before) so contrast audits
  // still see a passing fg/bg pair when text flips to brown.
  primary:
    "ubp-cta-button ubp-cta-button-primary border-2 border-[#b46c29] bg-[#b46c29] text-white hover:border-[#b46c29] hover:bg-white hover:text-[#b46c29] focus-visible:ring-[#b46c29]",
  secondary:
    "ubp-cta-button ubp-cta-button-secondary border-2 border-[#29361a] bg-white text-[#29361a] hover:border-[#29361a] hover:bg-[#29361a] hover:text-white focus-visible:ring-[#29361a]",
  outline:
    "ubp-cta-button ubp-cta-button-outline border-2 border-[#b46c29] bg-white text-[#b46c29] hover:border-[#b46c29] hover:bg-[#b46c29] hover:text-white focus-visible:ring-[#b46c29]",
  green:
    "ubp-cta-button ubp-cta-button-green border-2 border-[#29361a] bg-[#29361a] text-white hover:border-[#29361a] hover:bg-white hover:text-[#29361a] focus-visible:ring-[#29361a]",
  ghost:
    "ubp-cta-button ubp-cta-button-ghost border-2 border-transparent bg-transparent text-[#29361a] hover:bg-[#29361a] hover:text-white focus-visible:ring-[#29361a]",
};

const sizeClasses: Record<CtaButtonSize, string> = {
  sm: "h-10 px-5 text-xs",
  md: "h-12 px-6 text-sm",
  lg: "h-14 px-8 text-base",
};

function cx(...parts: Array<string | undefined | false>) {
  return parts.filter(Boolean).join(" ");
}

export function CtaButton({
  children,
  className,
  variant = "primary",
  size = "md",
  icon,
  iconPosition = "left",
  ...props
}: CtaButtonProps) {
  const classes = cx(
    "group inline-flex items-center justify-center gap-2 rounded-md font-heading font-bold uppercase tracking-wider focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    variantClasses[variant],
    sizeClasses[size],
    className,
  );

  const content = (
    <>
      {icon && iconPosition === "left" && (
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center bg-transparent transition-[color,filter] duration-500 ease-in-out">
          {icon}
        </span>
      )}
      <span className="transition-colors duration-500 ease-in-out">{children}</span>
      {icon && iconPosition === "right" && (
        <span className="flex-shrink-0">{icon}</span>
      )}
    </>
  );

  if ("opensQuoteModal" in props && props.opensQuoteModal) {
    return (
      <button type="button" data-quote-modal="" className={classes}>
        {content}
      </button>
    );
  }

  if ("opensZendeskChat" in props && props.opensZendeskChat) {
    return (
      <button type="button" data-open-zendesk="" className={classes}>
        {content}
      </button>
    );
  }

  if ("href" in props && typeof props.href === "string") {
    return (
      <Link href={props.href} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={props.type ?? "button"}
      onClick={props.onClick}
      disabled={props.disabled}
      className={classes}
    >
      {content}
    </button>
  );
}
